/**
 * EditProductDialog - Dialog para edição de produto no marketplace
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Funcionalidades:
 * - Edição de título, preço, descrição, localização
 * - Upload/gestão de imagens via ImageManager
 * - Seleção de condição do produto
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - axios (requisições API)
 * - Dialog, Button, Input, Label, Textarea, Select (shadcn/ui)
 * - ImageManager (componente extraído)
 */

import React, { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { useTranslation } from "../translations";
import ImageManager from "./ImageManager";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditProductDialog = ({ item, onUpdate }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: item.title || '',
    description: item.description || '',
    price: item.price || '',
    location: item.location || '',
    contact_info: item.contact_info || '',
    condition: item.condition || 'novo',
    images: item.images || []
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        // Converte formato brasileiro (42.000,00) para número
        price: parseFloat(String(formData.price).replace(/\./g, '').replace(',', '.'))
      };
      
      // In a real app, this would be PUT /api/marketplace/{item.id}
      // For now, let's simulate by deleting and recreating
      await axios.delete(`${API}/marketplace/${item.id}`);
      await axios.post(`${API}/marketplace`, submitData);
      
      setOpen(false);
      onUpdate();
      alert("Produto atualizado com sucesso!");
    } catch (error) {
      console.error("Error updating product:", error);
      alert("Erro ao atualizar produto: " + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          ✏️ {t('edit')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('editProduct')}</DialogTitle>
          <DialogDescription>{t('edModifyProduct')}</DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-title">{t('edProductTitle')} *</Label>
              <Input 
                id="edit-title" 
                value={formData.title} 
                onChange={(e) => setFormData({...formData, title: e.target.value})} 
                required 
              />
            </div>
            <div>
              <Label htmlFor="edit-price">{t('edPrice')} *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">R$</span>
                <Input 
                  id="edit-price" 
                  type="text"
                  inputMode="decimal"
                  className="pl-10"
                  value={formData.price} 
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^0-9.,]/g, '');
                    setFormData({...formData, price: value});
                  }}
                  onBlur={(e) => {
                    let value = e.target.value.replace(',', '.');
                    if (value && !isNaN(parseFloat(value))) {
                      setFormData({...formData, price: parseFloat(value).toFixed(2)});
                    }
                  }}
                  placeholder="42.000,00"
                  required 
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="edit-location">{t('edFullLocation')} *</Label>
            <Input 
              id="edit-location" 
              value={formData.location} 
              onChange={(e) => setFormData({...formData, location: e.target.value})} 
              required 
              placeholder="Ex: Fazenda Santa Rita, Km 15, Zona Rural, Acre, AC, CEP 69900-000"
            />
          </div>

          <div>
            <Label htmlFor="edit-description">{t('edProductDesc')} *</Label>
            <Textarea 
              id="edit-description" 
              value={formData.description} 
              onChange={(e) => setFormData({...formData, description: e.target.value})} 
              required 
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="edit-contact">{t('edContactInfo')} *</Label>
            <Input 
              id="edit-contact" 
              value={formData.contact_info} 
              onChange={(e) => setFormData({...formData, contact_info: e.target.value})} 
              required 
              placeholder="Telefone, WhatsApp, email..."
            />
          </div>

          <div>
            <Label>{t('edPhotosVideos')}</Label>
            <ImageManager
              images={formData.images}
              onImagesChange={(images) => {
                setFormData({...formData, images: images});
              }}
            />
          </div>

          <div>
            <Label htmlFor="edit-condition">{t('edCondition')} *</Label>
            <Select value={formData.condition} onValueChange={(value) => setFormData({...formData, condition: value})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="novo">{t('edNew')}</SelectItem>
                <SelectItem value="seminovo">{t('edSemiNew')}</SelectItem>
                <SelectItem value="usado">{t('edUsed')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex space-x-2 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? t('saving') : t('saveChanges')}
            </Button>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('cancel')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditProductDialog;
